import {generatePath} from '@legit-health/ui';
import {DiagnosticReportApiResponse, UserId, THREE_MINUTES, CompanyId} from '@legit-health/ui';
import {DIAGNOSTIC_REPORTS} from '@/shared/const/backend';
import {SubscribeToEventsOptions} from '@/shared/contexts/SubscribeToEventsContext/types';
import useApiQuery, {UseApiQueryOptions} from '@/shared/hooks/useApiQuery';
import PaginatedResults from '@/shared/interfaces/PaginatedResults';
import {EncounterId} from '../interfaces/Encounter';
import useSuspenseApiQuery from './useSuspenseApiQuery';

export type SearchParams = {
  practitionerId?: UserId;
  from?: string;
  to?: string;
  patientId?: UserId;
  companyId?: CompanyId;
  encounterId?: EncounterId;
  conditionId?: number | null;
  pathologyId?: number | null;
  bodySiteId?: number;
  page?: string;
  limit?: string;
  ids?: string[] | null;
  undiagnosed?: boolean;
  malignant?: boolean;
  mostLikelyPathology?: number | string | null;
  hasPriority?: boolean;
  isReviewed?: boolean;
  orderBy?: string;
};

export function getQueryKey(searchParams: SearchParams) {
  return [DIAGNOSTIC_REPORTS, searchParams];
}

export function useDiagnosticReports<TDataResponse = DiagnosticReportApiResponse>({
  searchParams,
  subscribeToEventsOptions,
  path = null,
  queryOptions
}: {
  searchParams: SearchParams;
  subscribeToEventsOptions?: SubscribeToEventsOptions;
  path?: string | null;
  queryOptions?: Omit<
    UseApiQueryOptions<PaginatedResults<TDataResponse>>['queryOptions'],
    'queryKey'
  >;
}) {
  const {
    practitionerId,
    from,
    to,
    patientId,
    conditionId,
    pathologyId,
    page,
    limit,
    ids,
    bodySiteId,
    undiagnosed,
    malignant,
    hasPriority,
    isReviewed,
    orderBy,
    mostLikelyPathology,
    companyId,
    encounterId
  } = searchParams;
  const urlParams = new URLSearchParams({
    ...{practitionerId: practitionerId ? practitionerId : ''},
    ...{companyId: companyId ? companyId : ''},
    ...{encounterId: encounterId ? encounterId : ''},
    ...{from: from ? from : ''},
    ...{to: to ? to : ''},
    ...{patientId: patientId ? patientId : ''},
    ...{page: page ? page : ''},
    ...{limit: limit ? limit : ''},
    ...{pathologyId: pathologyId ? `${pathologyId}` : ''},
    ...{mostLikelyPathology: mostLikelyPathology ? `${mostLikelyPathology}` : ''},
    ...{conditionId: conditionId ? `${conditionId}` : ''},
    ...{ids: ids ? ids.join(',') : ''},
    ...{bodySiteId: bodySiteId ? `${bodySiteId}` : ''},
    ...{undiagnosed: undiagnosed === undefined ? '' : undiagnosed ? '1' : '0'},
    ...{malignant: malignant === undefined ? '' : malignant ? '1' : '0'},
    ...{hasPriority: hasPriority === undefined ? '' : hasPriority ? '1' : '0'},
    ...{isReviewed: isReviewed === undefined ? '' : isReviewed ? '1' : '0'},
    ...{orderBy: orderBy ? orderBy : ''}
  });

  return useApiQuery<PaginatedResults<TDataResponse>>({
    queryOptions: {
      queryKey: getQueryKey(searchParams),
      staleTime: THREE_MINUTES,
      ...queryOptions
    },
    requestArguments: {
      method: 'GET',
      path: generatePath(path ?? DIAGNOSTIC_REPORTS, {}, urlParams)
    },
    subscribeToEventsOptions
  });
}

export function useSuspenseDiagnosticReports<TDataResponse = DiagnosticReportApiResponse>({
  searchParams,
  subscribeToEventsOptions,
  path = null,
  queryOptions
}: {
  searchParams: SearchParams;
  subscribeToEventsOptions?: SubscribeToEventsOptions;
  path?: string | null;
  queryOptions?: Omit<
    Parameters<typeof useSuspenseApiQuery<PaginatedResults<TDataResponse>>>[0]['queryOptions'],
    'queryKey'
  >;
}) {
  const {
    practitionerId,
    companyId,
    from,
    to,
    patientId,
    conditionId,
    pathologyId,
    page,
    limit,
    ids,
    bodySiteId,
    encounterId,
    undiagnosed,
    malignant,
    hasPriority,
    isReviewed,
    orderBy,
    mostLikelyPathology
  } = searchParams;
  const urlParams = new URLSearchParams({
    ...{practitionerId: practitionerId ? practitionerId : ''},
    ...{companyId: companyId ? companyId : ''},
    ...{from: from ? from : ''},
    ...{to: to ? to : ''},
    ...{patientId: patientId ? patientId : ''},
    ...{encounterId: encounterId ? encounterId : ''},
    ...{page: page ? page : ''},
    ...{limit: limit ? limit : ''},
    ...{pathologyId: pathologyId ? `${pathologyId}` : ''},
    ...{mostLikelyPathology: mostLikelyPathology ? `${mostLikelyPathology}` : ''},
    ...{conditionId: conditionId ? `${conditionId}` : ''},
    ...{ids: ids ? ids.join(',') : ''},
    ...{bodySiteId: bodySiteId ? `${bodySiteId}` : ''},
    ...{undiagnosed: undiagnosed === undefined ? '' : undiagnosed ? '1' : '0'},
    ...{malignant: malignant === undefined ? '' : malignant ? '1' : '0'},
    ...{hasPriority: hasPriority === undefined ? '' : hasPriority ? '1' : '0'},
    ...{isReviewed: isReviewed === undefined ? '' : isReviewed ? '1' : '0'},
    ...{orderBy: orderBy ? orderBy : ''}
  });

  return useSuspenseApiQuery<PaginatedResults<TDataResponse>>({
    queryOptions: {
      queryKey: getQueryKey(searchParams),
      staleTime: THREE_MINUTES,
      ...queryOptions
    },
    requestArguments: {
      method: 'GET',
      path: generatePath(path ?? DIAGNOSTIC_REPORTS, {}, urlParams)
    },
    subscribeToEventsOptions
  });
}
